import React, { useEffect, useRef, useState } from 'react';
import Joyride from 'react-joyride';
import { omit } from 'lodash';
import { styled, ClickAwayListener } from '@mui/material';

import { AIFeedbackOverlay, FilterSort, TaskDrawerInner } from 'components/organisms/Task';
import { useAuth } from 'contexts/auth';
import { useInstructor } from 'contexts/instructorRole';
import { useStudent } from 'contexts/studentRole';
import { useTask } from 'contexts/task';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import { useTour } from 'contexts/tour';
import withResponsiveLayout from 'hoc/withResponsive';
import KanbanMobile from 'pages/Tasks/Kanban/mobile';
import KanbanTablet from 'pages/Tasks/Kanban/tablet';
import KanbanDesktop from 'pages/Tasks/Kanban/desktop';
import TimelineMobile from 'pages/Tasks/Timeline/mobile';
import TimelineTablet from 'pages/Tasks/Timeline/tablet';
import TimelineDesktop from 'pages/Tasks/Timeline/desktop';
import { AlertDialog, Drawer } from 'shell/components';
import TourCompletionModal from 'components/organisms/Tour/TourCompletionModal';
import { TYPES } from 'utils/constants';

const renderFilterSortDrawer = (filterSort, assignableGroupMembers, onClick, onClose, resetFilterSort) => {
    return (
        <FilterSort
            {...filterSort}
            assignableGroupMembers={assignableGroupMembers}
            onClick={onClick}
            onClose={onClose}
            resetFilterSort={resetFilterSort}
        />
    );
};

const countActiveFilters = (filterSort) => {
    let count = 0;
    if (filterSort.sortKey !== 'none') count = count + 1;
    if (filterSort.workloadFilterKey.length > 0) count = count + 1;
    if (filterSort.assigneeFilterKey.length > 0) count = count + 1;
    if (filterSort.dueFilterKey.length > 0) count = count + 1;
    return count;
};

const viewOptions = [
    { key: 'task-board', value: 'Task Board', icon: 'taskBoard' },
    { key: 'timeline', value: 'Timeline', icon: 'timeline' },
];

const tooltipTexts = {
    toDo: {
        title: '"To Do" Column',
        description: 'Tasks that have been identified and prioritized for completion, but have not been started',
    },
    inProgress: {
        title: '"In Progress" Column',
        description: 'Tasks that are actively being worked on by team members',
    },
    underReview: {
        title: '"Under Review" Column',
        description: 'Tasks that are waiting for review or approval before being considered "done"',
    },
    completed: {
        title: '"Completed" Column',
        description: 'Tasks that have been reviewed and are now complete.',
    },
};

const TaskPage = () => {
    const { auth } = useAuth();
    const instructorContext = useInstructor();
    const studentContext = useStudent();
    const taskContext = useTask();
    const tourContext = useTour();
    const {
        isLoading,

        taskList,
        getTaskById,
        getTasks,
        createTask,
        updateTask,
        deleteTask,
        duplicateTask,
        moveTask,

        getSubtasksByTaskId,
        getSubtaskById,
        createSubtask,
        updateSubtask,
        deleteSubtask,

        fetchFeedbackByTaskId,
        getFeedbackByTaskId,
        addFeedback,
        updateFeedback,
        deleteFeedback,
        addSubfeedback,
        updateSubfeedback,
        deleteSubfeedback,
        countFeedback,

        fileList,
        deleteFile,
        countFiles,

        sharedUrlList,
        fetchSharedUrlsByTaskId,
        createTaskResource,
        deleteTaskResource,

        createActivity,

        isTaskDrawerOpen,
        setIsTaskDrawerOpen,
        taskDrawerContent,
        setTaskDrawerContent,

        filterSort,
        setSort,
        toggleWorkloadFilter,
        toggleAssigneeFilter,
        toggleDueFilter,
        onChangeSearchBarInput,
        resetFilterSort,

        aiFeedbackOverlay,
        setAIFeedbackOverlay,

        alertDialog,
        setAlertDialog,
        resetAlertDialog,
    } = tourContext?.tourOn ? tourContext : taskContext;
    const { currentGroupAccountUsers } = useUser2();
    const { device, width } = useViewport();

    const pageRef = useRef(null);

    const [view, setView] = useState(viewOptions[0].key);
    const [shouldCloseOnClickOutside, setShouldCloseOnClickOutside] = useState(false);
    const [subtaskState, setSubtaskState] = useState({
        isOpen: false,
        subtaskId: '',
    });
    const [submissionsState, setSubmissionsState] = useState({
        isOpen: false,
        mode: 'history',
        submissions: [],
    });
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

    const tasks = getTasks();
    const assignableGroupMembers = currentGroupAccountUsers;

    const openCreateTaskDrawer = (status = 'toDo', milestoneId = '') => {
        const task = createTask(status, milestoneId);
        setTaskDrawerContent({ task });
        setIsTaskDrawerOpen(true);
        if (tourContext?.tourOn) tourContext.handleClickNext();
    };

    const onOpenTask = (taskId) => {
        const isTask = Boolean(getTaskById(taskId));
        const isSubtask = Boolean(getSubtaskById(taskId));

        if (isTask) {
            const task = getTaskById(taskId);
            setTaskDrawerContent({ task });
            setIsTaskDrawerOpen(true);
            fetchFeedbackByTaskId(taskId);
            fetchSharedUrlsByTaskId(taskId);
            setSubtaskState({
                isOpen: false,
                subtaskId: '',
            });
        }

        if (isSubtask) {
            const subtask = getSubtaskById(taskId);
            const task = getTaskById(subtask.parent_id);
            setTaskDrawerContent({ task });
            setIsTaskDrawerOpen(true);
            fetchFeedbackByTaskId(task.id);
            fetchSharedUrlsByTaskId(task.id);
            setSubtaskState({
                isOpen: true,
                subtaskId: subtask.id,
            });
        }
    };

    const handleClickFilterSortOption = (kind, key) => {
        switch (kind) {
            case 'SORT':
                return setSort(key);
            case 'WORKLOAD':
                return toggleWorkloadFilter(key);
            case 'ASSIGNEE':
                return toggleAssigneeFilter(key);
            case 'DUE_DATE':
                return toggleDueFilter(key);
            default:
                return;
        }
    };

    const layoutProps = {
        viewOptions,
        view,
        setView,
        taskList: tasks,
        columns: [
            { droppableId: 'toDo', title: 'To Do', tasks: tasks.toDo },
            { droppableId: 'inProgress', title: 'In Progress', tasks: tasks.inProgress },
            { droppableId: 'underReview', title: 'Under Review', tasks: tasks.underReview },
            { droppableId: 'completed', title: 'Completed', tasks: tasks.completed },
        ],
        isFilterDrawerOpen,
        isTaskDrawerOpen,
        isSorted: filterSort.sortKey !== 'none',
        numFilters: countActiveFilters(filterSort),
        searchBarInput: filterSort.searchBarInput,
        onChangeSearchBarInput,
        openCreateTaskDrawer,
        openFilterDrawer: () => setIsFilterDrawerOpen(true),
        onDragEnd: ({ draggableId, source, destination }) => moveTask(draggableId, source, destination),
        onOpenTask,
        onDeleteTask: (id) => {
            setAlertDialog({
                visible: true,
                title: 'Permanently Delete Task?',
                content: `Are you sure you want to delete "${getTaskById(id).title}"?`,
                actionButtonText: 'Delete',
                variant: 'destructive',
                onConfirm: () => {
                    deleteTask(id);
                    resetAlertDialog();
                },
                onCancel: resetAlertDialog,
            });
        },
        onDuplicateTask: duplicateTask,
        updateTask,
        onMouseEnter: () => setShouldCloseOnClickOutside(false),
        onMouseLeave: () => setShouldCloseOnClickOutside(true),

        // Tooltip
        tooltipTexts,
    };

    const responsiveLayouts = {
        'task-board': {
            mobile: <KanbanMobile {...layoutProps} />,
            tablet: <KanbanTablet ref={pageRef} {...layoutProps} />,
            desktop: <KanbanDesktop ref={pageRef} {...layoutProps} />,
        },
        timeline: {
            mobile: <TimelineMobile {...layoutProps} />,
            tablet: <TimelineTablet ref={pageRef} {...layoutProps} />,
            desktop: <TimelineDesktop ref={pageRef} {...layoutProps} />,
        },
    }[view];

    const handleCloseDrawer = () => {
        if (device === 'mobile' && tourContext?.currentStep === 5) return;
        if (tourContext?.tourOn) tourContext.handleClickNext();
        return setIsTaskDrawerOpen(false);
    };

    const taskDrawerProps = {
        open: isTaskDrawerOpen,
        onClose: () => handleCloseDrawer(),
        className: 'tour-4',
        isViewingAIFeedback: aiFeedbackOverlay.open,
        isViewingSubmissions: submissionsState.isOpen,
        type: getTaskById(taskDrawerContent.task.id)?.type,
    };

    const taskDrawerInnerProps = {
        role: auth.role,
        isOpen: isTaskDrawerOpen,
        taskList,
        task: getTaskById(taskDrawerContent.task.id),
        currentAccountId: auth.owner_account_id,
        assignableGroupMembers,
        courseInstructors:
            auth.role === 'instructor' ? instructorContext.getCourseInstructors() : studentContext.courseInstructors,
        getUserByAccountId: (accountId) => {
            if (auth.role === 'instructor') {
                return instructorContext.getUserByAccountId(accountId);
            }
            const user = studentContext.getUserByAccountId(accountId);
            if (user) return user;
            return studentContext.courseInstructors.find((instructor) => instructor.owner_account_id === accountId);
        },
        openCreateTaskDrawer,
        onOpenTask,
        updateTask,
        deleteTask: (id) => {
            setAlertDialog({
                visible: true,
                title: 'Permanently Delete Task?',
                content: `Are you sure you want to delete "${getTaskById(id).title}"?`,
                actionButtonText: 'Delete',
                variant: 'destructive',
                onConfirm: () => {
                    setTimeout(() => {
                        deleteTask(id);
                    }, 200);
                    setIsTaskDrawerOpen(false);
                    resetAlertDialog();
                },
                onCancel: resetAlertDialog,
            });
        },
        duplicateTask: (taskId) => {
            const newTask = duplicateTask(taskId);
            setTaskDrawerContent({ task: newTask });
        },
        createTaskResource,
        deleteTaskResource: (resourceType, resourceId) => {
            setAlertDialog({
                visible: true,
                title: 'Permanently Delete resource?',
                content: `Are you sure you want to delete this resource?`,
                actionButtonText: 'Delete',
                variant: 'destructive',
                onConfirm: () => {
                    deleteTaskResource(resourceType, resourceId);
                    resetAlertDialog();
                },
                onCancel: resetAlertDialog,
            });
        },
        getSubtasksByTaskId,
        getSubtaskById,
        createSubtask,
        updateSubtask,
        deleteSubtask: (id) => {
            setAlertDialog({
                visible: true,
                title: 'Permanently Delete Subtask?',
                content: `Are you sure you want to delete "${getSubtaskById(id).title}"?`,
                actionButtonText: 'Delete',
                variant: 'destructive',
                onConfirm: () => {
                    deleteSubtask(id);
                    resetAlertDialog();
                },
                onCancel: resetAlertDialog,
            });
        },
        feedback: taskDrawerContent.task.id ? getFeedbackByTaskId(taskDrawerContent.task.id) : {},
        addFeedback,
        updateFeedback,
        deleteFeedback: (id) => {
            setAlertDialog({
                visible: true,
                title: 'Permanently Delete Feedback?',
                content: 'Are you sure you want to delete this feedback?',
                actionButtonText: 'Delete',
                variant: 'destructive',
                onConfirm: () => {
                    deleteFeedback(id);
                    resetAlertDialog();
                },
                onCancel: resetAlertDialog,
            });
        },
        addSubfeedback,
        updateSubfeedback,
        deleteSubfeedback: (id, parentId) => {
            setAlertDialog({
                visible: true,
                title: 'Permanently Delete Feedback?',
                content: 'Are you sure you want to delete this feedback?',
                actionButtonText: 'Delete',
                variant: 'destructive',
                onConfirm: () => {
                    deleteSubfeedback(id, parentId);
                    resetAlertDialog();
                },
                onCancel: resetAlertDialog,
            });
        },
        countFeedback,
        fileList: taskDrawerContent.task.id
            ? Object.values(fileList)
                  .filter((file) => file.active)
                  .filter((file) => {
                      switch (file.type) {
                          case TYPES.FEEDBACK:
                              return file.grand_parent_id === taskDrawerContent.task.id;
                          case TYPES.TASK:
                              return file.task_id === taskDrawerContent.task.id;
                          case TYPES.TEMPLATE:
                              return file.template_id === getTaskById(taskDrawerContent.task.id)?.template_id;
                          case TYPES.MILESTONE:
                              return file.milestone_id === getTaskById(taskDrawerContent.task.id)?.milestone_id;
                          default:
                              return false;
                      }
                  })
                  .reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), {})
            : {},
        deleteFile: (id) => {
            setAlertDialog({
                visible: true,
                title: 'Permanently Delete File?',
                content: 'Are you sure you want to remove this file?',
                actionButtonText: 'Delete',
                variant: 'destructive',
                onConfirm: () => {
                    deleteFile(id);
                    resetAlertDialog();
                },
                onCancel: resetAlertDialog,
            });
        },
        countFiles,
        sharedUrlList: taskDrawerContent.task.id
            ? Object.values(sharedUrlList)
                  .filter((sharedUrl) => sharedUrl.active)
                  .filter((sharedUrl) => {
                      switch (sharedUrl.type) {
                          case TYPES.TASK:
                              return sharedUrl.task_id === taskDrawerContent.task.id;
                          case TYPES.TEMPLATE:
                              return sharedUrl.template_id === getTaskById(taskDrawerContent.task.id)?.template_id;
                          case TYPES.MILESTONE:
                              return sharedUrl.milestone_id === getTaskById(taskDrawerContent.task.id)?.milestone_id;
                          default:
                              return false;
                      }
                  })
                  .reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), {})
            : {},
        createActivity: ({ id, title }, type, action) => {
            createActivity(omit({ ...getTaskById(id), title }, ['shouldCreateActivityOnBlur']), type, action);
        },
        onClose: () => handleCloseDrawer(),
        subtaskState,
        setSubtaskState,
        submissionsState,
        setSubmissionsState,
    };

    const responsiveTaskDrawers = {
        mobile: (
            <CustomDrawer device={'mobile'} {...taskDrawerProps}>
                <TaskDrawerInner device={'mobile'} {...taskDrawerInnerProps} />
            </CustomDrawer>
        ),
        tablet: (
            <CustomDrawer device={'tablet'} variant={'persistent'} {...taskDrawerProps}>
                <ClickAwayListener
                    onClickAway={(e) => {
                        if (shouldCloseOnClickOutside && pageRef.current.contains(e.target)) {
                            taskDrawerProps.onClose();
                        }
                    }}
                >
                    <TaskDrawerInner device={'tablet'} {...taskDrawerInnerProps} />
                </ClickAwayListener>
            </CustomDrawer>
        ),
        desktop: (
            <CustomDrawer device={'desktop'} variant={'persistent'} {...taskDrawerProps}>
                <ClickAwayListener
                    onClickAway={(e) => {
                        if (shouldCloseOnClickOutside && pageRef.current.contains(e.target)) {
                            taskDrawerProps.onClose();
                        }
                    }}
                >
                    <TaskDrawerInner device={'desktop'} {...taskDrawerInnerProps} />
                </ClickAwayListener>
            </CustomDrawer>
        ),
    };

    const filterDrawerProps = {
        open: isFilterDrawerOpen,
        onClose: () => setIsFilterDrawerOpen(false),
    };

    const responsiveFilterDrawers = {
        mobile: (
            <Drawer anchor={'left'} device={'mobile'} {...filterDrawerProps}>
                {renderFilterSortDrawer(
                    filterSort,
                    assignableGroupMembers,
                    handleClickFilterSortOption,
                    () => setIsFilterDrawerOpen(false),
                    resetFilterSort
                )}
            </Drawer>
        ),
        tablet: (
            <Drawer anchor={'left'} device={'tablet'} {...filterDrawerProps}>
                {renderFilterSortDrawer(
                    filterSort,
                    assignableGroupMembers,
                    handleClickFilterSortOption,
                    () => setIsFilterDrawerOpen(false),
                    resetFilterSort
                )}
            </Drawer>
        ),
        desktop: (
            // device is tablet in order to render the tablet (smaller) drawer
            <Drawer anchor={'left'} device={'tablet'} {...filterDrawerProps}>
                {renderFilterSortDrawer(
                    filterSort,
                    assignableGroupMembers,
                    handleClickFilterSortOption,
                    () => setIsFilterDrawerOpen(false),
                    resetFilterSort
                )}
            </Drawer>
        ),
    };

    useEffect(() => {
        return () => {
            setIsTaskDrawerOpen(false);
            setAIFeedbackOverlay({ open: false, aiFeedback: null });
        };
    }, []);

    return isLoading ? (
        <></>
    ) : (
        <>
            {withResponsiveLayout(width, responsiveLayouts)}
            {withResponsiveLayout(width, responsiveTaskDrawers)}
            {withResponsiveLayout(width, responsiveFilterDrawers)}
            {aiFeedbackOverlay.open && <AIFeedbackOverlay />}
            <AlertDialog
                actionButtonText={alertDialog.actionButtonText}
                description={alertDialog.content}
                message={alertDialog.title}
                open={alertDialog.visible}
                variant={alertDialog.variant}
                onAction={alertDialog.onConfirm}
                onCancel={alertDialog.onCancel}
                onClose={alertDialog.onCancel}
            />
            <Joyride
                disableBeacon={true}
                callback={tourContext.handleJoyrideCallback}
                continuous
                stepIndex={tourContext.currentStep}
                disableScrolling
                disableOverlayClose
                disableScrollParentFix
                hideCloseButton
                run={tourContext.run}
                steps={tourContext.steps}
                styles={{
                    options: {
                        zIndex: 10000,
                    },
                }}
            />
            <TourCompletionModal
                open={tourContext.tourCompleteModalOpen}
                handleClose={() => tourContext.handleCloseCompletionModal()}
                handleReset={() => tourContext.handleReset()}
            />
        </>
    );
};

const CustomDrawer = styled(Drawer)(({ isViewingAIFeedback, isViewingSubmissions, type }) => {
    return {
        ...(isViewingAIFeedback && {
            visibility: 'hidden',
        }),
        ...(!isViewingSubmissions &&
            type === TYPES.MILESTONE && {
                '& .MuiDrawer-paper > .MuiBox-root': {
                    marginBottom: '68px',
                },
            }),
    };
});

export default TaskPage;
